import { Divider, Flex } from "@chakra-ui/react";
import { isEmpty } from "lodash";
import { useMemo, useState } from "react";
import { useRecoilValue } from "recoil";

import { AutoRefillToggle } from "components/AutoRefillToggle";
import { PRODUCT_TYPES } from "components/constants";
import { OFIconButton } from "design_system/components/IconButton";
import { OFModal } from "design_system/components/Modal";
import { OFText } from "design_system/components/OFText";
import { EditIcon, TrashcanIcon } from "design_system/icons";
import { CalculateOrderSummaryModel, EvalIds, OrderStatus, PlatformType } from "graphql/generated";
import { useConfig } from "src/hooks";
import { platformState } from "src/states";
import {
  checkIsConsultOnly,
  checkIsRxOneTime,
  checkShouldChargeNow,
  isEDBundleProduct
} from "src/utils";

import { colors } from "../../../src/colors";
import CheckoutDueSection from "./CheckoutDueSection";
import { OrderSummaryItemHeader } from "./OrderSummaryItemHeader";
import { OrderSummaryItemWrapper } from "./OrderSummaryItemWrapper";
import { PriceSwitchRow } from "./PriceSwitchRow";

interface Props {
  t: (key: string, values?: any) => string;
  item: any;
  summary: CalculateOrderSummaryModel;
  onOpenCancelModal: (item: any) => void;
  onToggleAutoRenew: (item: any) => void;
  onTogglePriceClick: (item: any) => void;
  onEditPriceClick: (item: any) => void;
  allowAction?: boolean;
}

const PrescriptionItem = ({
  onOpenCancelModal,
  onTogglePriceClick,
  onToggleAutoRenew,
  onEditPriceClick,
  item,
  summary,
  t,
  allowAction
}: Props) => {
  const platform = useRecoilValue(platformState);
  const { price, product, oldOrder, type } = item;
  const { requires_prescription: requiresPrescription } = product.metadata;

  const isEDBundle = isEDBundleProduct(product);

  const { addSuffixCountry } = useConfig();
  const checkoutTrans = addSuffixCountry("checkout");
  const [openEDBundleInfo, setOpenEDBundleInfo] = useState(false);

  const isSecondOpinion = oldOrder?.status === OrderStatus.PrescriptionRejected;
  const isRenewal: boolean =
    !isEmpty(oldOrder) && oldOrder?.status !== OrderStatus.PrescriptionRejected;
  const isCancelledRenewal: boolean = isRenewal && oldOrder?.isCancelled === true;

  const isPrescription =
    requiresPrescription &&
    (type === PRODUCT_TYPES.SUBSCRIPTION || type === PRODUCT_TYPES.ONE_TIME);

  const isCovidConsult = useMemo(
    () => product?.evaluationId?.noah === EvalIds.CovidTeleconsult,
    [product?.evaluationId?.noah]
  );

  const showEditButton = useMemo(() => {
    if ((oldOrder?.subId && oldOrder?.status === OrderStatus.Active) || isCovidConsult) {
      return false;
    }

    return true;
  }, [isCovidConsult, oldOrder?.status, oldOrder?.subId]);

  const shouldChargeNow = useMemo(() => checkShouldChargeNow(product), [product]);

  const isConsultOnly = useMemo(() => {
    const evalId = product.evaluationId?.[platform || PlatformType.Noah] as EvalIds;

    return checkIsConsultOnly(evalId, product.uniqName);
  }, [product, platform]);

  const isRxOneTime = useMemo(() => checkIsRxOneTime(product), [product]);

  const priceItemSummary = summary?.itemList?.find((it) => it?.priceId === price?.id);

  return (
    <OrderSummaryItemWrapper key={item.price.id}>
      <OrderSummaryItemHeader item={item} />

      {type === PRODUCT_TYPES.BOTH && allowAction && (
        <PriceSwitchRow item={item} onTogglePriceClick={onTogglePriceClick} />
      )}

      {!isConsultOnly && !isRxOneTime && allowAction && (
        <AutoRefillToggle
          mb="16px"
          isAutoRefill={item?.isAutoRenewal}
          onToggle={() => onToggleAutoRenew(item)}
        />
      )}

      {((isPrescription && isEmpty(oldOrder)) ||
        (isCancelledRenewal && isPrescription) ||
        isSecondOpinion) && (
        <CheckoutDueSection
          price={priceItemSummary?.itemSubTotalAfterDiscount || 0}
          shouldChargeNow={shouldChargeNow}
          isEDBundle={isEDBundle}
        />
      )}

      {allowAction && (
        <>
          <Divider m="16px 0px 8px 0px" borderColor={colors.grey[500]} />

          <Flex w="100%" justifyContent="flex-end" gap="16px">
            <OFIconButton
              icon={<TrashcanIcon />}
              onClick={() => {
                onOpenCancelModal(item);
              }}
            />

            {!shouldChargeNow && showEditButton && (
              <OFIconButton icon={<EditIcon />} onClick={() => onEditPriceClick(item)} />
            )}
          </Flex>
        </>
      )}

      {isEDBundle && (
        <OFModal
          isOpen={openEDBundleInfo}
          onClose={() => {
            setOpenEDBundleInfo(false);
          }}
        >
          <Flex flexDirection="column">
            <OFText textStyle="h4" borderBottom={`1px solid ${colors.grey[500]}`}>
              {t(`${checkoutTrans}:ed-trial-bundle-info.title`)}
            </OFText>

            <OFText textStyle="body1" mt="24px">
              {t(`${checkoutTrans}:ed-trial-bundle-info.description`)}
            </OFText>
          </Flex>
        </OFModal>
      )}
    </OrderSummaryItemWrapper>
  );
};

export default PrescriptionItem;
